import { nextTick, onBeforeUnmount, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { findSnapshot } from '@/composables/usePageSnapshot'
import { childByClass } from '@/utils/active-page'

/**
 * Retour arrière au glissé depuis le bord gauche, comme sur iOS.
 *
 * Le geste ne met plus rien en scène lui-même : il suit le doigt, puis PASSE
 * LA MAIN à la transition de page déclarative (usePageTransition). Un glissé
 * validé et un appui sur le bouton retour produisent donc exactement la même
 * chose, ce qui est le seul moyen fiable qu'ils se ressemblent.
 *
 * Comment la main se passe : le style en ligne l'emporte sur les classes que
 * `<Transition>` applique. On écrit donc sur la page sortante une transition
 * en ligne qui part de la POSITION DU DOIGT et finit hors cadre ; Vue la garde
 * vivante le temps de cette transition (il lit la durée sur le style calculé
 * et attend son `transitionend`), pendant qu'il fait entrer la vraie page
 * précédente. On rattrape juste la parallaxe de celle-ci là où le doigt l'a
 * laissée, sinon elle sauterait en arrière à son quart de largeur au moment
 * précis du relâchement.
 *
 * Sous la page tirée, pendant la traction, on pose l'INSTANTANÉ de l'écran
 * précédent, gardé de côté au moment où on l'a quitté (usePageSnapshot). Il
 * avance à un quart de la vitesse du doigt, exactement comme la vraie page le
 * fera. Au relâchement, celle-ci arrive et reprend la course à la position de
 * la copie, qu'on retire dans la même image : la substitution ne se voit pas.
 * Sans instantané rangé pour cette destination, le geste marche quand même, on
 * découvre simplement le fond de l'app.
 *
 * Ne s'arme que si tout est réuni : le doigt part du bord, la page affiche un
 * bouton retour, et aucune feuille n'est ouverte (dans une modale, ce geste
 * appartient à la modale).
 *
 * Le bouton retour est à la fois la CONDITION et l'ACTION. La condition, parce
 * qu'une page qui n'en affiche pas n'a nulle part où revenir : sur une racine
 * d'onglet, le geste ne doit rien faire du tout. L'action, parce que ces
 * boutons ne font pas tous la même chose (`fallback` qui remplace l'entrée
 * courante, `goBack` maison, `router.back()` par défaut) : on clique celui de
 * la page plutôt que de naviguer nous-mêmes, et le glissé fait exactement ce
 * que ferait le doigt sur le bouton.
 */

/** Largeur de la zone de départ, en pixels. Au-delà, c'est un geste de page. */
const EDGE_WIDTH = 25
/** Marque posée par BackButton, seule affordance de retour reconnue. */
const BACK_BUTTON = '[data-back-button]'
/** Course avant qu'un mouvement compte comme une traction. */
const THRESHOLD = 8
/** Fraction de la largeur au-delà de laquelle on valide au relâchement. */
const COMMIT_RATIO = 0.4
/** Vitesse (px/ms) qui valide quelle que soit la distance parcourue. */
const COMMIT_VELOCITY = 0.45
/**
 * Durée de la sortie et du rappel. La même que les transitions de page dans
 * index.css : la sortante et l'entrante doivent avancer du même pas, sinon
 * l'une finit sa course pendant que l'autre glisse encore.
 */
const ANIM_MS = 340
const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'
/** Parallaxe de la page entrante. À garder égale au `-25%` d'index.css. */
const PARALLAX = 0.25
/** Ombre du bord gauche, la même que `.app-page.page-back-leave-active`. */
const EDGE_SHADOW = '-8px 0 24px rgb(0 0 0 / 0.18)'
/** Filet quand une garde de route refuse la navigation. */
const NAV_TIMEOUT_MS = 600

function clearInline(el: HTMLElement) {
  el.style.transition = ''
  el.style.transform = ''
  el.style.willChange = ''
  el.style.boxShadow = ''
}

export function useSwipeBack(): void {
  const router = useRouter()

  let page: HTMLElement | null = null
  let backButton: HTMLElement | null = null
  let startX = 0
  let startY = 0
  let startTime = 0
  let armed = false
  let dragging = false
  let width = 0
  /** Calque de la sortie courante, tant qu'un instantané y est posé. */
  let layer: HTMLElement | null = null
  /**
   * Jeton du calque courant. Il n'y a qu'un calque par sortie, et les rangements
   * différés d'un geste annulé ne doivent pas effacer celui du geste suivant.
   */
  let layerToken = 0

  /**
   * Où le bouton retour va, pour savoir quel écran poser dessous. `data-back-to`
   * quand il remplace l'entrée courante par un `fallback`, l'entrée précédente
   * de l'historique sinon.
   */
  function backTarget(trigger: HTMLElement): string | null {
    const declared = trigger.dataset.backTo
    if (declared) return declared
    return (window.history.state as { back?: string | null } | null)?.back ?? null
  }

  /** Décalage horizontal courant du calque, en pixels, animation comprise. */
  function layerOffset(el: HTMLElement): number {
    const parent = el.parentElement
    if (!parent) return 0
    return el.getBoundingClientRect().left - parent.getBoundingClientRect().left
  }

  function showSnapshot(outlet: HTMLElement, trigger: HTMLElement) {
    const snapshot = findSnapshot(backTarget(trigger))
    const found = childByClass(outlet, 'page-snapshot')
    if (!snapshot || !found) return
    found.replaceChildren(snapshot)
    found.style.display = 'block'
    found.style.transition = 'none'
    found.style.transform = `translateX(${-width * PARALLAX}px)`
    layer = found
    layerToken += 1
  }

  function hideSnapshot(token?: number) {
    if (token !== undefined && token !== layerToken) return
    const el = layer
    layer = null
    if (!el) return
    el.style.display = ''
    el.style.transition = ''
    el.style.transform = ''
    el.replaceChildren()
  }

  function activePage(target: EventTarget | null): HTMLElement | null {
    const el = target instanceof HTMLElement ? target.closest('.app-page') : null
    return (el as HTMLElement | null) ?? document.querySelector<HTMLElement>('.app-page')
  }

  function onTouchStart(ev: TouchEvent) {
    armed = false
    dragging = false
    backButton = null
    if (ev.touches.length > 1) return
    const touch = ev.touches[0]
    if (touch.clientX > EDGE_WIDTH) return
    // Une feuille ouverte capte le geste pour elle : c'est elle qu'on renvoie.
    if (document.querySelector('.app-sheet-panel')) return

    page = activePage(ev.target)
    if (!page) return
    // Pas de bouton retour, pas de retour : la page ne bouge pas d'un pixel.
    backButton = page.querySelector<HTMLElement>(BACK_BUTTON)
    if (!backButton) return
    startX = touch.clientX
    startY = touch.clientY
    startTime = Date.now()
    width = window.innerWidth || 1
    armed = true
  }

  function onTouchMove(ev: TouchEvent) {
    if (!armed || !page || ev.touches.length > 1) return
    const dx = ev.touches[0].clientX - startX
    const dy = ev.touches[0].clientY - startY

    if (!dragging) {
      if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return
      // Plus vertical qu'horizontal, ou vers la gauche : ce n'est pas un retour.
      if (Math.abs(dy) > Math.abs(dx) || dx <= 0) {
        armed = false
        return
      }
      dragging = true
      page.style.transition = 'none'
      page.style.willChange = 'transform'
      page.style.boxShadow = EDGE_SHADOW
      if (page.parentElement && backButton) showSnapshot(page.parentElement, backButton)
    }

    if (ev.cancelable) ev.preventDefault()
    const travelled = Math.max(0, dx)
    page.style.transform = `translateX(${travelled}px)`
    // Un quart de la vitesse du doigt : la même parallaxe que la vraie page.
    if (layer) {
      const progress = Math.min(1, travelled / width)
      layer.style.transform = `translateX(${-width * PARALLAX * (1 - progress)}px)`
    }
  }

  /**
   * Rattrape la page entrante à la position que le doigt lui donnait. Sa
   * classe `page-back-enter-from` la pose à son quart de largeur ; si on la
   * laissait faire, elle reculerait d'un coup au relâchement.
   */
  function catchIncoming(outlet: HTMLElement, outgoing: HTMLElement, progress: number, since: number) {
    const incoming = Array.from(outlet.children)
      .find((el) => el.classList.contains('app-page') && el !== outgoing) as HTMLElement | undefined
    if (!incoming) {
      hideSnapshot()
      return
    }

    // Si un instantané est posé, il est DÉJÀ en train de glisser : la vraie
    // page reprend à sa position exacte, pas à celle du relâchement, sinon la
    // parallaxe ferait un pas en arrière au moment de la substitution.
    const from = layer ? layerOffset(layer) : -width * PARALLAX * (1 - progress)
    const remaining = Math.max(80, ANIM_MS - (Date.now() - since))

    incoming.style.transition = 'none'
    incoming.style.transform = `translateX(${from}px)`
    // Dans la même image que le placement : rien ne clignote entre les deux.
    hideSnapshot()
    requestAnimationFrame(() => {
      incoming.style.transition = `transform ${remaining}ms ${EASING}`
      incoming.style.transform = 'translateX(0)'
      window.setTimeout(() => clearInline(incoming), remaining)
    })
  }

  function commit(dx: number, outgoing: HTMLElement, trigger: HTMLElement) {
    const outlet = outgoing.parentElement
    const progress = Math.min(1, dx / width)
    const since = Date.now()

    outgoing.style.transition = `transform ${ANIM_MS}ms ${EASING}`
    outgoing.style.transform = `translateX(${width}px)`

    // Le calque finit sa course sans attendre la navigation : la parallaxe ne
    // doit pas se figer le temps que la vraie page se monte.
    if (layer) {
      layer.style.transition = `transform ${ANIM_MS}ms ${EASING}`
      layer.style.transform = 'translateX(0)'
    }

    let navigated = false
    const stopWaiting = router.afterEach(() => {
      navigated = true
      stopWaiting()
      void nextTick(() => {
        if (outlet) catchIncoming(outlet, outgoing, progress, since)
        else hideSnapshot()
      })
    })

    // Garde de route qui refuse : sans ce filet, la page resterait hors cadre
    // et l'écran serait vide.
    window.setTimeout(() => {
      stopWaiting()
      if (navigated || !outgoing.isConnected) return
      hideSnapshot()
      outgoing.style.transform = 'translateX(0)'
      window.setTimeout(() => clearInline(outgoing), ANIM_MS)
    }, NAV_TIMEOUT_MS)

    trigger.click()
  }

  function onTouchEnd(ev: TouchEvent) {
    const el = page
    const trigger = backButton
    page = null
    backButton = null

    if (!dragging || !el) {
      armed = false
      return
    }
    const dx = (ev.changedTouches[0]?.clientX ?? startX) - startX
    const velocity = dx / Math.max(1, Date.now() - startTime)
    armed = false
    dragging = false

    if (trigger && (dx > width * COMMIT_RATIO || velocity > COMMIT_VELOCITY)) {
      commit(dx, el, trigger)
      return
    }

    el.style.transition = `transform ${ANIM_MS}ms ${EASING}`
    el.style.transform = 'translateX(0)'
    window.setTimeout(() => clearInline(el), ANIM_MS)

    if (layer) {
      const token = layerToken
      layer.style.transition = `transform ${ANIM_MS}ms ${EASING}`
      layer.style.transform = `translateX(${-width * PARALLAX}px)`
      window.setTimeout(() => hideSnapshot(token), ANIM_MS)
    }
  }

  onMounted(() => {
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', onTouchEnd)
    document.addEventListener('touchcancel', onTouchEnd)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('touchstart', onTouchStart)
    document.removeEventListener('touchmove', onTouchMove)
    document.removeEventListener('touchend', onTouchEnd)
    document.removeEventListener('touchcancel', onTouchEnd)
  })
}
